import $ from 'jquery'
import _ from 'lodash'
import GroupForm from './group-form'
import GroupDatasets from './group-datasets'
import GroupList from './group-list'
import Group from './group'
import groupType from './group-type'
import AdminTwoColumnEditor from 'scoreboard/admin/admin-two-column-editor'
import OrgGroupPermissionsForm from './org-group-permissions-form'
import appDisplay from 'scoreboard/components/app-display'
import conductor from 'scoreboard/components/conductor'
import guard from 'scoreboard/components/unsaved/guard'
import i18n from 'scoreboard/components/i18n'
import LabeledToggle from 'scoreboard/components/labeled-toggle'
import OrgGardener from 'scoreboard/organizations/organizations-gardener'
import projects from 'scoreboard/projects/projects'
import TwoColumnDetailsForm from 'scoreboard/components/two-column-details-form'
import mobile from 'scoreboard/components/mobile'
import scrollContainer from 'scoreboard/components/scroll-container'
import { Transform } from 'stream'
import CopyGroupDialog from 'scoreboard/admin/principals/groups/copy-group-dialog'
import DependenciesPreventActionView from 'scoreboard/components/edit/dependencies-prevent-action-view'
import errorHandler from 'scoreboard/components/error-handler'
import dialogStack from 'scoreboard/components/dialog-stack'
import ConfirmPrompt from 'scoreboard/components/confirm-prompt'
import selects from 'scoreboard/components/selects'

/**
 * @extends AdminTwoColumnEditor
 */
const AdminGroups = AdminTwoColumnEditor.extend(/** @lends AdminGroups.prototype */{

	urlRoot: 'admin/groups',
	Model: Group,
	SelectorView: GroupList,
	DisplayView: GroupForm,
	className: AdminTwoColumnEditor.prototype.className + ' admin-groups two-column-details-form',
	heading: 'common.group.groupsAndPermissions',
	/**
	 * If the server should validate the group, includes:
	 * 1. warning for dataset's that the group can't view based on org permission
	 */
	validate: true,

	events: _.extend({}, AdminTwoColumnEditor.prototype.events, {
		'click .copy': 'onClickCopy'
	}),

	/**
	 * @param {FormViewOptions} options.formViewOptions
	 */
	initialize: function(options ={}) {
		options.formViewOptions = {
			guardOptions: {
				keepChangesFn: () => this.submit()
			}
		}
		AdminTwoColumnEditor.prototype.initialize.apply(this, arguments)
	},

	render: function() {
		AdminTwoColumnEditor.prototype.render.apply(this, arguments)
		this._renderLabeledToggle()
		this._renderOrgGardener()
		this.setupTwoColumnDetailsForm()
		this._onToggle()
		this._onOrgSelect({id: this.options.orgId})
		this._setupListeners()
		this.twoColumnsDetailsForm.resizeLeftPanel()
		// display copy button
		this.$('.move-copy').removeClass('is-not-displayed')
		return this
	},

	add: function() {
		this._toggleLoadingDots(true)
		$.get('api/admin/groups/new').then(groupData => {
			this._toggleLoadingDots(false)
			AdminTwoColumnEditor.prototype.add.call(this, groupData)
		})
	},

	// opens the "copy group" dialog (#13874)
	onClickCopy: function () {
		guard.conditionallyRun(() => {
			appDisplay.dialog(this.itemPromise, () => {
				let model = _.cloneDeep(this.item)
				let copyGroupDialog = new CopyGroupDialog({model})
				copyGroupDialog.on('done', (data) => {
					// we don't need a save guard on the new group yet
					guard.stopGuarding()
					// navigate to the appropriate item
					// this will be the new group if "copy and edit" was selected
					// otherwise we will remain on the previous group
					this.changeItem(data.itemId)
					// make sure the group list styling reflects the change
					this.selectorView.setSelectedState(data.itemId)
				})
				return copyGroupDialog
			})
		})
	},

	_setupListeners: function() {
		this.orgGardener.on('select', this._onOrgSelect.bind(this))
		this.orgGardener.on('projectId', projectId => {
			this.orgGardener.show({ projectId })
			// Select the root org so the object selector is updated for the new project
			const orgId = projects.get(projectId).get('orgId')
			if (orgId) {
				this._onOrgSelect({id: orgId})
			} else {
				this.orgGardener.displayNoOrgs()
				this.$('.gardener .tree-container').addClass('tree-container-light')
			}
		})

		// Listen for when labeled toggles are toggled
		this.listenTo(this.toggle, 'change', () => {
			const chooseBy = this._onToggle()
			if (chooseBy === 'group') {
				conductor.navigate(this.urlRoot)
			} else if (chooseBy === 'organization') {
				conductor.navigate(this.urlRoot + '/organizations')
			}
		})

		this.on('resize', () => {
			this.twoColumnsDetailsForm.resizeLeftPanel()
			this.twoColumnsDetailsForm.adjustLeftPanelScroll()
		})

		this.on('error', this.onError.bind(this))

		// Every time a item is added or selected, refresh the size of the
		// left group-list panel.
		this.selectorView.on('change:item add', () => {
			this.twoColumnsDetailsForm.resizeLeftPanel()
		})
	},

	_renderLabeledToggle: function() {
		this.toggle = new LabeledToggle({
			options: [{
				value: 'group',
				label: i18n.get('common.byWithArg', i18n.get('common.group.group'))
			}, {
				value: 'organization',
				label: i18n.get('common.byWithArg', i18n.get('common.organization.organization'))
			}],
			value: this.options.byOrg ? 'organization' : 'group',
			fullWidth: true
		})
		this.$('.admin-list .group-panel .panel-content').prepend(this.toggle.el)
		this.toggle.render()
	},

	_renderOrgGardener: function() {
		var projectsVisible = projects.isVisible()

		let AdminGroupOrgGardener = OrgGardener.extend({
			_treeOptions: function() {
				var treeOptions = OrgGardener.prototype._treeOptions.apply(this, arguments)
				var map = new Transform({ objectMode: true })
				map._transform = function (node, enc, next) {
					if (node.view === false) {
						node.className = 'is-disabled'
					}
					this.push(node)
					next()
				}
				treeOptions.stream = treeOptions.stream.pipe(map)
				return treeOptions
			}
		})

		this.orgGardener = new AdminGroupOrgGardener({
			showProjectSelector: projectsVisible,
			permissions: true
		})

		this.orgGardener.$el
			.addClass('tree-selector-form')
			.toggleClass('has-project-selector', projectsVisible)

		this.$('.admin-list .group-panel .panel-content').append(this.orgGardener.el)
		this.orgGardener.render()
	},

	setupTwoColumnDetailsForm: function() {
		this.twoColumnsDetailsForm = new TwoColumnDetailsForm(_.extend({}, {
			el: this.el,
			gardener: this.orgGardener,
			getScrollContainer: () => {
				if (mobile.isMobileWidth()) {
					return this.$('.display-layout')
				} else {
					return scrollContainer(this.el)
				}
			}
		}, this._getTwoColumnDetailsFormOptions()))

		// We need to make sure we handle adjusting the left pane when we're
		// scrolling in the mobile orientation and when we're not. One listener has
		// already been setup in TwoColumnDetailsForm._setupListeners; figure out
		// which one and setup the other listener here for when we cross from
		// desktop -> mobile or vice versa.
		if (mobile.isMobileWidth()) {
			scrollContainer(this.el)
				.on('scroll', () => this.twoColumnsDetailsForm.adjustLeftPanelScroll())
		} else {
			this.$('.display-layout')
				.on('scroll', () => this.twoColumnsDetailsForm.adjustLeftPanelScroll())
		}
	},

	/**
	 * @inheritDoc
	 */
	gatherData: function() {
		// #14914 #22754 restrict permissions to those appropriate to the group type
		this.displayView.setPermissionsForGroupType(this.item)

		// #53438 trim down the datasets to just the required data
		let datasets = _.map(this.item.get('datasets'), d => {
			return _.pick(d, ['id', 'label', 'organizationId',
				'projectId', 'permissions', 'type'])
		})

		let data = AdminTwoColumnEditor.prototype.gatherData.apply(this, arguments)
		data = _.extend({}, this.item.attributes, data)
		data.validate = this.validate
		data.datasets = datasets
		return data
	},

	submit: function(options) {
		// #54745 if switching to a lower group type, confirm with the user that
		// it's OK that they may lose some permissions
		let oldType = this.originalItem.get('type')
		let newType = this.displayView.readFromDom().type
		return checkReducedGroupPermissions(this, oldType, newType, options)
	},

	doSubmit: function(options) {
		let type = this.item.isNew() ? 'POST' : 'PUT'
		let data = this.gatherData()
		this.itemPromise = $.ajax({
			url: this.item.url(),
			type,
			data: JSON.stringify(data),
			success: (response) => {
				var event = (this.item.isNew() ? 'add' : 'edit')
					+ ':domain-object'
				// #54821 don't overwrite the full dataset data
				this.item.set(_.omit(data, 'datasets'))
				// #54841 the info from the server is the most correct so
				// update it last so that it takes precedence over any data from
				// before
				this.item.set(response)
				this.onSaveSuccess(options, event)
			},
			error: (xhr) => this.handleError(this.item, xhr)
		})
		return this.itemPromise
	},

	_onToggle: function() {
		// Clear the right side and remove its views
		_.invoke(this.displayView, 'remove')
		this.$('.admin-display').empty()
		// Toggle the class which shows/hides the proper .group-list panel content
		// based on the selected toggle value
		const chooseBy = this.toggle.val()
		this.$el.removeClass('showing-choose-by-group showing-choose-by-organization')
			.addClass(`showing-choose-by-${chooseBy}`)

		// Clear any floating that might be applied to the left pane and reset its height
		this.twoColumnsDetailsForm.adjustLeftPanelScroll(0)
		this.twoColumnsDetailsForm.resizeLeftPanel()
		return chooseBy
	},

	// Override renderItem() in order to make sure we are resizing the group list panel
	// when something new happens inside it.
	renderSelectedItem: function(model) {
		AdminTwoColumnEditor.prototype.renderSelectedItem.apply(this, arguments)
		this.twoColumnsDetailsForm.resizeLeftPanel()
	},

	_onOrgSelect: function(node={}) {
		if (node.view === false) return

		const orgId = node.id
		this.$('.gardener .tree-container').addClass('tree-container-light')
		if (orgId) {
			_.invoke(this.displayView, 'remove')

			this._toggleLoadingDots(true)
			$.get(`api/organizations/${orgId}/groups`).then(orgGroupPermissions => {
				this._toggleLoadingDots(false)
				this.displayView = new OrgGroupPermissionsForm(orgGroupPermissions)
				this.displayView.on('change', this.formView.change.bind(this.formView))
				this.displayView.on('toggleExpandCollapse', () => {
					this.twoColumnsDetailsForm.resizeLeftPanel()
					this.twoColumnsDetailsForm.adjustLeftPanelScroll(0)
				})
				this.$('.admin-display').empty().append(this.displayView.el)
				this.displayView.render()
				conductor.navigate(this.urlRoot + '/organizations/' + orgId)
				this.orgGardener._onceTreeRendered(() => this.twoColumnsDetailsForm.setupTreeExpander())
			})
		}
	},

	/**
	 * callback after a item has been deleted
	 * @param item
	 * @private
	 */
	_deleteItem: function(item) {
		// If we're viewing permissions by org there is nothing to do,
		// and we DO NOT want to clear out the right display pane
		if (this.toggle.val() === 'group') {
			AdminTwoColumnEditor.prototype._deleteItem.apply(this, arguments)
		}
	},

	/**
	 * Invoked before [handleError]
	 *
	 * @param model
	 * @param xhr
	 */
	onError: function(model, xhr) {
		if (this._actionFailed(xhr)) {
			return // don't do anything to the form, user has the option to resubmit
		}
		model.set(_.pick(this.originalItem.attributes, 'members', 'admins'), {silent: true})
		this.renderSelectedItem(model)
	},

	/**
	 * Need to do some custom error handling here for non-viewable datasets.
	 *
	 * @param model
	 * @param xhr
	 */
	handleError: function(model, xhr) {
		errorHandler(xhr, {
			formView: this.formView,
			/**
			 * The fact that prevented actions return 400s makes this a bit clunky since
			 * we have to allow the user to continue with a save and navigation (continueFn) but I
			 * couldn't think of a response code that was any more appropriate (considered 204)
			 */
			handler: (xhr) => {
				if (this._actionFailed(xhr)) {
					const continueFn = guard.getCurrentContinueFn()
					this.preventedActionView = new DependenciesPreventActionView({
						model,
						formView: this.formView,
						showDontSave: dialogStack.hasViewType(GroupDatasets),
						header: null, // prevents header from rendering
						title: i18n.get('datasets.warning.nonviewable'),
						response: xhr.responseJSON,
						useIcon: true
					})
					this.preventedActionView.on('save', () => {
						this.validate = false
						this.submit({navigate: false}).then(() => {
							// Turn validations back on for any future saves
							this.validate = true
							continueFn && continueFn()
						})
					})
					this.preventedActionView.on('close', () => {
						// The form is still dirty, we must mark it as such (#37141)
						// formView.start sets dirty=false on nextTick so we must do it here too :(
						process.nextTick(() => this.formView.setDirty())
					})
					appDisplay.replaceDialog(this.preventedActionView)
					return true
				}
			}
		})
	},

	_actionFailed: function(xhr) {
		return _.has(xhr.responseJSON, 'actionFailedReason')
	},

	remove: function () {
		AdminTwoColumnEditor.prototype.remove.apply(this, arguments)
		_.invokeMap([this.orgGardener, this.toggle, this.twoColumnsDetailsForm], 'remove')
	}
})

/**
 * Checks whether the user is reducing permission access when changing group type.
 * If so, prompt the user to confirm the change.
 * @param view - the view checking for reduced permissions
 * @param oldType - the old group type
 * @param newType - the new group type
 * @param options - the options to pass to the submit function
 * @returns {*}
 */
function checkReducedGroupPermissions(view, oldType, newType, options) {
	if (isLesserGroupType(oldType, newType)) {
		let prompt = new ConfirmPrompt({
			titleKey: 'admin.group.changeGroupType',
			messageKey: 'admin.group.changeGroupType.message',
			confirmLabel: 'admin.group.changeGroupType',
			cancelLabel: 'common.cancel'
		})
		appDisplay.dialog(prompt)
		prompt.once('cancel', () => {
			// #54853 change the group type back
			selects.val(view.$('[name=type]'), oldType)
			// re-enable the form controls
			view.formView.resetForm()
			// enable the submit button
			view.formView.change()
		})
		const deferred = $.Deferred()
		prompt.once('done', () => {
			view.doSubmit(options).then(() =>
				deferred.resolve())
		})
		return deferred
	} else {
		return view.doSubmit(options)
	}
}

function isLesserGroupType(oldGroupType, newGroupType) {
	let oldIndex = groupType.accessLevelOrder.indexOf(oldGroupType)
	let newIndex = groupType.accessLevelOrder.indexOf(newGroupType)
	return oldIndex > -1 && newIndex > -1 && oldIndex < newIndex
}

export default AdminGroups
export { checkReducedGroupPermissions, isLesserGroupType }

AdminGroups.__filename__ = 'admin/principals/groups/admin-groups.js'
